import React from 'react';
import { CategoryDynamicComponent } from '../../types/dynamicComponents';
import { parseMultiplicationTableConfig } from './definitions';

interface DynamicMultiplicationTableProps {
  component: CategoryDynamicComponent;
  selectedNumber: number | undefined;
  onSelectionChange: (componentId: number, value: number) => void;
}

const DynamicMultiplicationTable: React.FC<DynamicMultiplicationTableProps> = ({
  component,
  selectedNumber,
  onSelectionChange,
}) => {
  const introHtmlValue = component.introHtml?.trim();
  const captionOverrideValue = component.captionOverride?.trim();
  const tableConfig = parseMultiplicationTableConfig(component.configJson);
  const currentNumber = selectedNumber ?? tableConfig.defaultNumber;

  const numberButtons = Array.from(
    { length: tableConfig.maxNumber - tableConfig.minNumber + 1 },
    (_, index) => tableConfig.minNumber + index
  );

  const multiplierRows = Array.from(
    { length: tableConfig.multiplierMax - tableConfig.multiplierMin + 1 },
    (_, index) => tableConfig.multiplierMin + index
  );

  const midpoint = Math.ceil(multiplierRows.length / 2);
  const leftColumnRows = multiplierRows.slice(0, midpoint);
  const rightColumnRows = multiplierRows.slice(midpoint);

  return (
    <div className="border border-indigo-200 rounded-xl bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-4 shadow-sm">
      {introHtmlValue && (
        <div
          className="html-content text-sm text-gray-700 leading-relaxed text-left mb-3"
          dangerouslySetInnerHTML={{ __html: introHtmlValue }}
        />
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        {numberButtons.map((value) => (
          <button
            key={`${component.id}-${value}`}
            type="button"
            onClick={() => onSelectionChange(component.id, value)}
            className={`px-3 py-1 rounded-full border text-sm font-semibold transition-all ${
              currentNumber === value
                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white border-blue-600 shadow'
                : 'bg-white text-gray-700 border-indigo-200 hover:bg-indigo-50'
            }`}
          >
            {value}
          </button>
        ))}
      </div>

      <div className="border border-indigo-200 rounded-lg overflow-hidden bg-white">
        <div style={{ background: '#222', color: '#fff', padding: '6px 12px', fontSize: 13, fontWeight: 700, textAlign: 'center', letterSpacing: '0.03em' }}>
          Table of {currentNumber}
        </div>
        <div className="grid grid-cols-2">
        <div className="border-r border-gray-100">
          {leftColumnRows.map((multiplier, index) => (
            <div
              key={`${component.id}-left-row-${multiplier}`}
              className={`px-3 py-2 text-sm font-medium ${index % 2 === 0 ? 'bg-indigo-50 text-indigo-900' : 'bg-white text-gray-700'} ${
                index === leftColumnRows.length - 1 ? '' : 'border-b border-gray-100'
              }`}
            >
              {currentNumber} × {multiplier} = {currentNumber * multiplier}
            </div>
          ))}
        </div>
        <div>
          {rightColumnRows.map((multiplier, index) => (
            <div
              key={`${component.id}-right-row-${multiplier}`}
              className={`px-3 py-2 text-sm font-medium ${index % 2 === 0 ? 'bg-blue-50 text-blue-900' : 'bg-white text-gray-700'} ${
                index === rightColumnRows.length - 1 ? '' : 'border-b border-gray-100'
              }`}
            >
              {currentNumber} × {multiplier} = {currentNumber * multiplier}
            </div>
          ))}
        </div>
      </div>
      </div>

      {captionOverrideValue && <p className="mt-2 mb-0 text-xs text-gray-600 text-center">{captionOverrideValue}</p>}
    </div>
  );
};

export default DynamicMultiplicationTable;
